import React, { useState } from 'react';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import { MetaResourceRenderer } from 'Components/UI/Meta/MetaForm/Components/MetaResourceRenderer';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import { useTheme } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import {
  FDBox,
  FDClickableText, FDContentContainer,
  FDPaper,
  FDStack,
} from '@facet-wealth/design';
import fr from 'facet-rest';
import { dataAttrs } from 'Helpers/Testing';
import { displayErrorMessageWithChecks } from 'Context/SnackbarContext/utils';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import RoadmapDeliverableTimeline
  from 'Views/Page/Content/Templates/WizardTemplates/RoadmapDeliverable/WelcomeToYourRoadmapStepTemplate/RoadmapDeliverableTimeline';
import LoadingIndicator from 'Components/UI/LoadingIndicator';
import useEventTrackingLogEvent, {
  categories,
  eventSubTypes,
  eventTypes,
} from 'EventTracking/useEventTrackingLogEvent';
import useUserContext from 'Context/UserContext';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import {
  RoadmapExecutiveSummaryActions,
} from 'Views/Page/Content/Templates/RoadmapExecutiveSummaryTemplate/RoadmapExecutiveSummary/RoadmapExecutiveSummaryActions/RoadmapExecutiveSummaryActions';
import RoadmapExecutiveSummaryActionsMetaOverrideSet
  from 'Views/Page/Content/Templates/RoadmapExecutiveSummaryTemplate/RoadmapExecutiveSummary/RoadmapExecutiveSummaryActions/metaOverrideSet';
import {
  SelectScenarioContextProvider,
} from 'Views/Page/Content/Templates/WizardTemplates/ScenarioSelectionRedefined/ScenarioSelectionRedefinedWrapperTemplate/SelectScenarioContext';
import { noop } from 'Helpers/PropTypes';
import { useRightColumnContext } from 'Context/RightColumnContext';
import { PlannerInsightChatLayout } from '../PlannerInsightChat/layout';
import PlannerInsightChatMetaOverrideSet from '../PlannerInsightChat/metaOverrideSet';
import { RoadmapDeliverableWrapperLayout } from '../Wrapper/RoadmapDeliverableWrapperLayout';

const Layout = () => {
  const [loading, setLoading] = useState(false);
  const [assumptionsLoading, setAssumptionsLoading] = useState(false);
  const { resource } = useMetaLayoutContext();
  const { displayErrorMessage } = useSnackbarContext();
  const { loadModalCollectionByLink } = useModalContext();
  const theme = useTheme();
  const { logStandardizedEvent, logStandardizedEventFailureWithError } = useEventTrackingLogEvent();
  const { isAgent, userResource } = useUserContext();
  const { loadRightColumnCollectionLink } = useRightColumnContext();

  const roadmapDeliverableIntroWizardRel = resource.withRelationship(
    fr.relationships.roadmapDeliverableIntroWizard.name,
    (rel) => rel,
    () => null,
  );

  useComponentDidMount(() => {
    if (roadmapDeliverableIntroWizardRel) {
      loadModalCollectionByLink(roadmapDeliverableIntroWizardRel.link('related').url());
    }
  });

  if (!resource) {
    return null;
  }

  const eventProperties = {
    resourceType: resource?.type(),
    isAgent,
    userId: userResource?.id(),
  };

  const disclaimerRel = resource.withRelationship(
    fr.relationships.disclaimer.name,
    (rel) => rel,
    () => null,
  );

  const onDisclaimerClick = async () => {
    logStandardizedEvent(categories.roadmapDeliverable, eventTypes.clickClickableText, 'click dislaimer assumptions link', { eventProperties });
    setAssumptionsLoading(true);
    try {
      await loadModalCollectionByLink(disclaimerRel.link('related').url());
      logStandardizedEvent(categories.roadmapDeliverable, eventTypes.clickClickableText, eventSubTypes.success, { eventProperties });
    } catch (err) {
      displayErrorMessageWithChecks(err, displayErrorMessage, 'Failed to open link.');
      logStandardizedEventFailureWithError(categories.roadmapDeliverable, eventTypes.clickClickableText, eventSubTypes.failure, err, { eventProperties });
    } finally {
      setAssumptionsLoading(false);
    }
  };

  const PlannerInsightChat = (
    <MetaResourceRenderer
      resource={resource} // has attrs for cfp insight
      layout={PlannerInsightChatLayout}
      metaOverrideSet={PlannerInsightChatMetaOverrideSet}
    />
  );

  const portfolioRel = resource.withRelationship(
    fr.relationships.portfolio.name,
    (rel) => rel,
    () => null,
  );

  const actionsSection = resource.attr('sections')?.resources().head();

  const ActionsSectionComponent = actionsSection ? (
    <SelectScenarioContextProvider onSelectSuccess={noop}>
      <MetaResourceRenderer
        resource={actionsSection}
        layout={RoadmapExecutiveSummaryActions}
        metaOverrideSet={RoadmapExecutiveSummaryActionsMetaOverrideSet}
        LayoutProps={{
          portfolioRel,
          isRoadmapDeliverable: true,
          setLoading,
        }}
      />
    </SelectScenarioContextProvider>

  ) : null;

  const timelineItems = resource.attr('timelineItems').resources().list();

  const handlePreviousOverride = resource.withRelationship(fr.relationships.messagePlanner.name,
    (rel) => async () => {
      logStandardizedEvent(categories.roadmapDeliverable, eventTypes.clickButton, 'open message planner drawer', { eventProperties });
      await loadRightColumnCollectionLink(rel.link('related'));
    },
    () => undefined);
  const secondaryActionLabelOverride = resource.withRelationship(fr.relationships.messagePlanner.name,
    (rel) => rel.meta('prompt') || 'Message my planner',
    () => undefined);

  return (
    <FDStack direction={'column'} spacing={1}>
      <RoadmapDeliverableWrapperLayout
        disabled={loading}
        handlePreviousOverride={handlePreviousOverride}
        secondaryActionLabelOverride={secondaryActionLabelOverride}
      >
        <FDStack spacing={5} direction="column" width={'100%'} maxWidth={600}>
          {PlannerInsightChat}
          <FDPaper appearance={'flat'}>
            <FDStack spacing={4} paddingX={3} paddingY={5} direction="column">
              <FDStack direction="column" spacing={2}>
                <MetaControlField attr={'welcomeTitle'} />
                <MetaControlField attr={'welcomeDescription'} />
              </FDStack>
              <RoadmapDeliverableTimeline timelineItems={timelineItems} />
              {ActionsSectionComponent}
            </FDStack>
          </FDPaper>
        </FDStack>
      </RoadmapDeliverableWrapperLayout>
      { disclaimerRel ? (
        <FDContentContainer maxWidth={'md'}>
          <FDBox>
            <FDStack direction={'column'} spacing={2}>
              {actionsSection && (
                <span
                  style={{
                    fontSize: '12px',
                    textAlign: 'center',
                    display: 'block',
                    color: theme.palette.facet.actionActive,
                  }}
                >
                  {actionsSection.attr('disclaimerText')}&nbsp;
                </span>
              )}
              <span
                style={{
                  paddingBottom: '40px',
                  fontSize: '12px',
                  textAlign: 'center',
                  display: 'block',
                  color: theme.palette.facet.actionActive,
                }}
              >
                {disclaimerRel.meta('prePrompt')}&nbsp;
                {assumptionsLoading ? <span style={{ display: 'inline-block', width: '54px' }}><LoadingIndicator spacing={false} size={10} /></span> : (
                  <FDClickableText
                    appearance={'standalone'}
                    label={disclaimerRel.meta('prompt')}
                    onClick={async () => onDisclaimerClick()}
                    {...dataAttrs('disclaimer-clickable-text')}
                  />
                )}&nbsp;
                {disclaimerRel.meta('postPrompt')}
              </span>
            </FDStack>
          </FDBox>
        </FDContentContainer>
      ) : null}
    </FDStack>
  );
};

export default Layout;
